import Joyride, { CallBackProps, ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import type { FeatureGuideState } from "./useFeatureGuide";

interface FeatureGuideProps {
	/** Guide state from useFeatureGuide(). */
	guide: FeatureGuideState;
	/** Joyride step definitions. */
	steps: Step[];
	/** Z-index for the overlay (default 10000). */
	zIndex?: number;
	/** Called when the tour finishes or is dismissed. */
	onComplete?: () => void;
}

/**
 * Reusable on-demand feature guide powered by react-joyride.
 *
 * Unlike the legacy GuidedTour (cookie-gated, global), this is designed
 * for contextual "Show Me" tours triggered by the user from within a feature.
 *
 * Usage:
 * ```tsx
 * const guide = useFeatureGuide();
 * <button onClick={guide.start}>Show Me</button>
 * <FeatureGuide guide={guide} steps={panelMeetingSteps} />
 * ```
 */
export const FeatureGuide: React.FC<FeatureGuideProps> = ({
	guide,
	steps,
	onComplete,
	zIndex = 10000,
}) => {
	const handleCallback = (data: CallBackProps) => {
		const { status, type, action } = data;

		if (action === ACTIONS.CLOSE) {
			guide.stop();
			onComplete?.();
			return;
		}

		if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
			guide.stop();
			onComplete?.();
			return;
		}

		if (type === EVENTS.STEP_AFTER) {
			if (action === ACTIONS.NEXT) {
				guide.next();
			} else if (action === ACTIONS.PREV) {
				guide.prev();
			}
		}
	};

	return (
		<Joyride
			steps={steps}
			run={guide.isRunning}
			stepIndex={guide.stepIndex}
			continuous
			showProgress
			showSkipButton
			callback={handleCallback}
			scrollToFirstStep
			scrollOffset={100}
			disableOverlayClose
			spotlightClicks={false}
			spotlightPadding={6}
			styles={{
				options: {
					arrowColor: "#fff",
					backgroundColor: "#fff",
					overlayColor: "rgba(0, 0, 0, 0.4)",
					primaryColor: "#4F46E5",
					spotlightShadow: "0 0 0 4px rgba(79, 70, 229, 0.6)",
					textColor: "#333",
					width: 380,
					zIndex,
				},
				spotlight: {
					borderRadius: "6px",
				},
			}}
			locale={{
				back: "Back",
				close: "Got it",
				last: "Got it",
				next: "Next",
				skip: "Skip tour",
			}}
			floaterProps={{
				disableAnimation: false,
			}}
		/>
	);
};
